import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Capture } from 'app/core/models/capture.model';
import { PatientPrescriberOffice } from 'app/core/models/patient-prescriber-office.model';
import { ProviderOffice } from 'app/core/models/provider-office.model';
import { AuthenticationService } from 'app/core/services/authentication.service';
import { PatientPrescriberOfficesService } from 'app/core/services/patient-prescriber-offices.service';
import { ProviderService } from 'app/core/services/provider.service';
import { keyedStatusOptions as keyedProviderOfficeStatusOptions } from 'app/core/options/provider-office-status.opts';
import { UploadService } from 'app/core/services/upload.service';
import { HttpErrorResponse } from '@angular/common/http';
import { PatientAttachmentsService } from 'app/core/services/patient-attachments.service';
import { PatientAttachment } from 'app/core/models/patient-attachment.model';
import { mergeMap } from 'rxjs/operators';
import { PatientNameComponent } from '../../../shared/components/patient-name/patient-name.component';
import { PrescriberNameComponent } from '../../../shared/components/prescriber-name/prescriber-name.component';

import { OfficeComponent } from '../../../shared/components/office/office.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { PatientAttachmentAttachmentSourceOptions } from 'app/core/options/patient-attachment-attachment-source.opts';
import { PatientAttachmentAttachmentSource } from 'app/core/enums/patient-attachment-attachment-source.enum';

@Component({
  selector: 'app-upload-consult-note-modal',
  templateUrl: './upload-consult-note-modal.component.html',
  styleUrls: ['./upload-consult-note-modal.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    PatientNameComponent,
    PrescriberNameComponent,
    OfficeComponent,
    MatProgressSpinner
  ]
})
export class UploadConsultNoteModalComponent implements OnInit {
  private uploadService = inject(UploadService);
  activeModal = inject(NgbActiveModal);
  private formBuilder = inject(FormBuilder);
  private authService = inject(AuthenticationService);
  private providerService = inject(ProviderService);
  private patientPrescriberOfficesService = inject(PatientPrescriberOfficesService);
  private patientAttachmentsService = inject(PatientAttachmentsService);

  capture: Capture;
  uploading = false;
  formGroup: FormGroup;
  errorMessage: string;
  loadingProviderOffices = true;
  loadingPatientPrescriberOffices = true;
  canViewPatient = false;
  providerOffices: ProviderOffice[] = [];
  patientPrescriberOffices: { [providerOfficeId: string]: PatientPrescriberOffice } = {};
  keyedProviderOfficeStatusOptions = keyedProviderOfficeStatusOptions;
  sourceOptions = PatientAttachmentAttachmentSourceOptions;

  file: File = null;
  fileType = '.pdf';

  ngOnInit() {
    this.loadProviderOffices();
    this.loadPatientPrescriberOffices();

    this.canViewPatient = this.authService.isCaptureAdminUser;

    this.formGroup = this.formBuilder.group({
      officeId: [null, Validators.required],
      source: [PatientAttachmentAttachmentSource.nuvemArs, Validators.required]
    });
  }

  onCloseClick() {
    this.activeModal.close();
  }

  onSubmit($event: SubmitEvent) {
    $event.preventDefault();

    const patientId = this.capture.patient.id;
    const prescriberId = this.capture.prescriber.id;
    const officeId = this.formGroup.value.officeId;
    const attachmentSource = this.formGroup.value.source;

    this.uploading = true;

    this.patientAttachmentsService.generateFileName(patientId, prescriberId, attachmentSource).
      pipe(
        mergeMap(result => {
          const renamedFile = new File([this.file], result.filename, {
            type: this.file.type,
            lastModified: this.file.lastModified,
          });

          return this.uploadService.upload(renamedFile);
        }),
        mergeMap(upload => {
          return this.patientAttachmentsService.
            create(attachmentSource, patientId, upload.signedId, officeId, prescriberId)
        })
      ).
      subscribe({
        next: (patientAttachment: PatientAttachment) => {
          this.activeModal.close(patientAttachment);
        },
        error: (error: HttpErrorResponse) => {
          this.uploading = false;
          this.errorMessage = "There was an error uploading the file."
          console.error(error);
        }
      });
  }

  onFileChange(event: Event): void {
    const fileInput = event.target as HTMLInputElement;

    if (fileInput.files && fileInput.files.length > 0) {
      this.file = fileInput.files[0];
    }
  }

  formValid() {
    return this.file && !this.formGroup.invalid;
  }

  fileUploadDisabled() {
    return this.uploading || this.providerOffices.length == 0;
  }

  private loadProviderOffices() {
    this.loadingProviderOffices = true;

    this.providerService.
      getProviderOffices(this.capture.provider.id).
      subscribe((providerOffices: ProviderOffice[]) => {
        this.providerOffices = providerOffices;

        this.loadingProviderOffices = false;
      });
  }

  private loadPatientPrescriberOffices() {
    this.loadingPatientPrescriberOffices = true;

    this.patientPrescriberOfficesService.
      getList(this.capture.patient.id, this.capture.prescriber.id).subscribe(response => {
        response.patientPrescriberOffices.forEach(ppo => {
          if (ppo.providerOfficeId) {
            this.patientPrescriberOffices[ppo.providerOfficeId] = ppo;
          }
        });
        this.loadingPatientPrescriberOffices = false;
      });
  }

  patientPrescriberOfficeStatus(providerOffice: ProviderOffice) {
    const ppo = this.patientPrescriberOffices[providerOffice.id];

    if (ppo) {
      return ppo.status;
    }
  }
}
